/**
 * Bundle Builder Component
 * Handles product selection, quantity management, and cart functionality
 * Includes free gift product for subscription orders
 */

if (!customElements.get('bundle-builder')) {
  class BundleBuilder extends HTMLElement {
    constructor() {
      super();
      
      this.selectedItems = []; // Array of {productId, variantId, title, price, image, tier, handle, quantity}
      this.totalItems = parseInt(this.dataset.totalItems) || 5;
      this.subscriptionDiscount = parseInt(this.dataset.subscriptionDiscount) || 10;
      this.purchaseType = 'subscription'; // 'subscription' or 'onetime'
      this.frequency = '14';
      this.pickRandomFallback = this.dataset.pickRandomFallback !== 'false';
      
      // Free gift product settings
      this.freeGiftEnabled = this.dataset.freeGiftEnabled === 'true';
      this.freeGiftVariantId = this.dataset.freeGiftVariantId || null;
      this.freeGiftProductTitle = this.dataset.freeGiftProductTitle || 'Free Gift';
      
      // Parse pre-selected products for "Pick for me" from customizer
      this.pickForMeProducts = [];
      try {
        const pickData = this.dataset.pickForMeProducts;
        if (pickData) {
          this.pickForMeProducts = JSON.parse(pickData);
        }
      } catch (e) {
        console.warn('Could not parse pick-for-me products:', e);
      }
      
      this.init();
    }

    init() {
      this.cacheElements();
      this.bindEvents();
      this.updateUI();
    }

    cacheElements() {
      // Product cards
      this.productCards = this.querySelectorAll('.bundle-product-card');
      
      // Desktop elements
      this.selectedCountEl = this.querySelector('[data-selected-count]');
      this.remainingTextEl = this.querySelector('[data-remaining-text]');
      this.completeTextEl = this.querySelector('[data-complete-text]');
      this.progressBar = this.querySelector('[data-progress-bar]');
      this.slotsContainer = this.querySelector('[data-slots-container]');
      this.slots = this.querySelectorAll('.bundle-builder__slot');
      this.checkoutSection = this.querySelector('[data-checkout-section]');
      this.selectMoreBtn = this.querySelector('[data-select-more-btn]');
      this.selectMoreBtnMobile = this.querySelector('[data-select-more-btn-mobile]');
      this.cartBtn = this.querySelector('[data-add-bundle-to-cart]');
      this.cartBtnMobile = this.querySelector('[data-add-bundle-to-cart-mobile]');
      this.cartBtnText = this.querySelector('[data-cart-btn-text]');
      this.bundlePriceEl = this.querySelector('[data-bundle-price]');
      this.bundleCompareEl = this.querySelector('[data-bundle-compare]');
      this.frequencyWrapper = this.querySelector('[data-frequency-wrapper]');
      this.frequencySelect = this.querySelector('[data-frequency-select]');
      
      // Free gift elements
      this.freeGiftBadge = this.querySelector('[data-free-gift-badge]');
      this.freeGiftBadgeMobile = this.querySelector('[data-free-gift-badge-mobile]');
      
      // Mobile elements
      this.mobileToggle = this.querySelector('[data-mobile-toggle]');
      this.mobileContent = this.querySelector('[data-mobile-content]');
      this.mobileSelectedCount = this.querySelector('[data-mobile-selected-count]');
      this.mobileRemaining = this.querySelector('[data-mobile-remaining]');
      this.mobileProgressBar = this.querySelector('[data-mobile-progress-bar]');
      this.mobileSlots = this.querySelectorAll('.bundle-builder__mobile-slot');
      this.mobileCheckout = this.querySelector('[data-mobile-checkout]');
      this.mobileCartBtn = this.querySelector('[data-mobile-add-to-cart]');
      this.mobileCartText = this.querySelector('[data-mobile-cart-text]');
      this.mobileFrequencyWrapper = this.querySelector('[data-mobile-frequency-wrapper]');
      this.mobileFrequencySelect = this.querySelector('[data-mobile-frequency-select]');
      this.mobilePriceEl = document.querySelector('[data-mobile-price]');
      this.mobileComparePriceEl = document.querySelector('[data-mobile-compare-price]');
      // Pick for me buttons
      this.pickForMeBtns = this.querySelectorAll('[data-pick-for-me]');
      
      // Purchase option buttons
      this.purchaseOptionBtns = this.querySelectorAll('[data-purchase-option]');
      this.mobilePurchaseOptionBtns = this.querySelectorAll('[data-mobile-purchase-option]');
    }

    bindEvents() {
      // Product card events
      this.productCards.forEach(card => {
        const addBtn = card.querySelector('[data-add-to-bundle]');
        const decreaseBtn = card.querySelector('[data-decrease-qty]');
        const increaseBtn = card.querySelector('[data-increase-qty]');
        
        if (addBtn) {
          addBtn.addEventListener('click', () => this.addProduct(card));
        }
        
        if (decreaseBtn) {
          decreaseBtn.addEventListener('click', () => this.decreaseQuantity(card));
        }
        
        if (increaseBtn) {
          increaseBtn.addEventListener('click', () => this.increaseQuantity(card));
        }
      });
      
      // Pick for me buttons
      this.pickForMeBtns.forEach(btn => {
        btn.addEventListener('click', () => this.pickForMe());
      });
      
      // Desktop slot remove buttons
      this.slots.forEach(slot => {
        const removeBtn = slot.querySelector('[data-remove-slot]');
        if (removeBtn) {
          removeBtn.addEventListener('click', () => {
            const productId = slot.dataset.productId;
            if (productId) {
              this.removeProduct(productId);
            }
          });
        }
      });
      
      // Mobile slot remove buttons
      this.mobileSlots.forEach(slot => {
        const removeBtn = slot.querySelector('[data-mobile-remove-slot]');
        if (removeBtn) {
          removeBtn.addEventListener('click', () => {
            const productId = slot.dataset.productId;
            if (productId) {
              this.removeProduct(productId);
            }
          });
        }
      });
      
      // Mobile toggle
      if (this.mobileToggle) {
        this.mobileToggle.addEventListener('click', () => this.toggleMobilePanel());
      }
      
      // Purchase options
      this.purchaseOptionBtns.forEach(btn => {
        btn.addEventListener('click', () => this.setPurchaseType(btn.dataset.purchaseOption));
      });
      
      this.mobilePurchaseOptionBtns.forEach(btn => {
        btn.addEventListener('click', () => this.setPurchaseType(btn.dataset.mobilePurchaseOption));
      });
      
      // Frequency selects
      if (this.frequencySelect) {
        this.frequencySelect.addEventListener('change', (e) => {
          this.frequency = e.target.value;
          if (this.mobileFrequencySelect) {
            this.mobileFrequencySelect.value = e.target.value;
          }
        });
      }
      
      if (this.mobileFrequencySelect) {
        this.mobileFrequencySelect.addEventListener('change', (e) => {
          this.frequency = e.target.value;
          if (this.frequencySelect) {
            this.frequencySelect.value = e.target.value;
          }
        });
      }
      
      // Add to cart buttons
      if (this.cartBtn) {
        this.cartBtn.addEventListener('click', () => this.addToCart());
      }
      
      if (this.mobileCartBtn) {
        this.mobileCartBtn.addEventListener('click', () => this.addToCart());
      }
    }

    addProduct(card) {
      const totalSelected = this.getTotalSelectedCount();
      
      if (totalSelected >= this.totalItems) {
        return; // Bundle is full
      }
      
      const productId = card.dataset.productId;
      const existingItem = this.selectedItems.find(item => item.productId === productId);
      
      if (existingItem) {
        existingItem.quantity += 1;
      } else {
        this.selectedItems.push({
          productId: productId,
          variantId: card.dataset.variantId,
          sellingPlan: card.dataset.sellingPlan,
          sellingId: card.dataset.sellingId,
          title: card.dataset.productTitle,
          price: parseInt(card.dataset.productPrice),
          comparePrice: parseInt(card.dataset.productComparePrice) || parseInt(card.dataset.productPrice),
          image: card.dataset.productImage,
          tier: card.dataset.productTier,
          handle: card.dataset.productHandle,
          barColor: card.dataset.productBarColor,
          quantity: 1
        });
      }
      
      this.updateUI();
      this.updateCardUI(card);
    }

    removeProduct(productId) {
      const index = this.selectedItems.findIndex(item => item.productId === productId);
      if (index > -1) {
        this.selectedItems.splice(index, 1);
      }
      
      const card = this.querySelector(`[data-product-id="${productId}"]`);
      if (card) {
        this.resetCardUI(card);
      }
      
      this.updateUI();
    }

    decreaseQuantity(card) {
      const productId = card.dataset.productId;
      const item = this.selectedItems.find(item => item.productId === productId);
      
      if (item) {
        item.quantity -= 1;
        
        if (item.quantity <= 0) {
          this.removeProduct(productId);
        } else {
          this.updateUI();
          this.updateCardUI(card);
        }
      }
    }

    increaseQuantity(card) {
      const totalSelected = this.getTotalSelectedCount();
      
      if (totalSelected >= this.totalItems) {
        return; // Bundle is full
      }
      
      const productId = card.dataset.productId;
      const item = this.selectedItems.find(item => item.productId === productId);
      
      if (item) {
        item.quantity += 1;
        this.updateUI();
        this.updateCardUI(card);
      }
    }

    getTotalSelectedCount() {
      return this.selectedItems.reduce((sum, item) => sum + item.quantity, 0);
    }

    pickForMe() {
      // Clear current selection
      this.selectedItems = [];
      this.productCards.forEach(card => this.resetCardUI(card));
      
      // Check if we have pre-selected products from customizer
      if (this.pickForMeProducts && this.pickForMeProducts.length > 0) {
        // Use pre-selected products
        let remaining = this.totalItems;
        
        this.pickForMeProducts.forEach(product => {
          if (remaining <= 0) return;
          
          // Find the matching card to verify product exists in collection
          const card = this.querySelector(`[data-product-id="${product.productId}"]`);
          
          if (card) {
            const quantity = Math.min(product.quantity || 1, remaining);
            
            this.selectedItems.push({
              productId: product.productId,
              variantId: product.variantId,
              sellingId: product.sellingId,
              sellingPlan: card.dataset.sellingPlan,
              title: product.title,
              price: parseInt(product.price),
              comparePrice: parseInt(card.dataset.productComparePrice) || parseInt(card.dataset.productPrice),
              image: product.image,
              tier: product.tier,
              handle: product.handle,
              barColor: card.dataset.productBarColor,
              quantity: quantity
            });
            
            remaining -= quantity;
          }
        });
        
        // If pre-selected products don't fill the bundle, fill remaining with random products
        if (remaining > 0 && this.pickRandomFallback) {
          const selectedIds = this.selectedItems.map(item => item.productId);
          const availableCards = Array.from(this.productCards).filter(
            card => !selectedIds.includes(card.dataset.productId)
          );
          
          const shuffled = availableCards.sort(() => Math.random() - 0.5);
          let index = 0;
          
          while (remaining > 0 && index < shuffled.length) {
            const card = shuffled[index];
            const quantity = Math.min(remaining, 1);
            
            this.selectedItems.push({
              productId: card.dataset.productId,
              variantId: card.dataset.variantId,
              title: card.dataset.productTitle,
              sellingPlan: card.dataset.sellingPlan,
              price: parseInt(card.dataset.productPrice),
              image: card.dataset.productImage,
              tier: card.dataset.productTier,
              handle: card.dataset.productHandle,
              barColor: card.dataset.productBarColor,
              quantity: quantity
            });
            
            remaining -= quantity;
            index++;
          }
        }
      } else {
        // Fallback to random selection if no pre-selected products
        const availableCards = Array.from(this.productCards);
        const shuffled = availableCards.sort(() => Math.random() - 0.5);
        
        let remaining = this.totalItems;
        let index = 0;
        
        while (remaining > 0 && index < shuffled.length) {
          const card = shuffled[index];
          const quantity = Math.min(remaining, Math.ceil(Math.random() * 2)); // Random 1-2 quantity
          
          this.selectedItems.push({
            productId: card.dataset.productId,
            variantId: card.dataset.variantId,
            sellingPlan: card.dataset.sellingPlan,
            sellingId: card.dataset.sellingId,
            title: card.dataset.productTitle,
            price: parseInt(card.dataset.productPrice),
            image: card.dataset.productImage,
            tier: card.dataset.productTier,
            handle: card.dataset.productHandle,
            barColor: card.dataset.productBarColor,
            quantity: quantity
          });
          
          remaining -= quantity;
          index++;
        }
        
        // Adjust if we went over
        if (remaining < 0) {
          const lastItem = this.selectedItems[this.selectedItems.length - 1];
          lastItem.quantity += remaining; // remaining is negative, so this subtracts
        }
      }
      
      // Update all card UIs
      this.selectedItems.forEach(item => {
        const card = this.querySelector(`[data-product-id="${item.productId}"]`);
        if (card) {
          this.updateCardUI(card);
        }
      });
      
      this.updateUI();
      
      // Auto-expand mobile panel
      if (this.mobileContent && this.mobileContent.classList.contains('hidden')) {
        this.toggleMobilePanel();
      }
    }

    updateCardUI(card) {
      const productId = card.dataset.productId;
      const item = this.selectedItems.find(item => item.productId === productId);
      
      const addBtn = card.querySelector('[data-add-to-bundle]');
      const qtyWrapper = card.querySelector('.bundle-product-card__quantity');
      const qtyNumber = card.querySelector('[data-qty-number]');
      
      if (item && item.quantity > 0) {
        card.classList.add('is-selected');
        if (addBtn) addBtn.classList.add('hidden');
        if (qtyWrapper) qtyWrapper.classList.remove('hidden');
        if (qtyNumber) qtyNumber.textContent = item.quantity;
      } else {
        this.resetCardUI(card);
      }
    }

    resetCardUI(card) {
      const addBtn = card.querySelector('[data-add-to-bundle]');
      const qtyWrapper = card.querySelector('.bundle-product-card__quantity');
      const qtyNumber = card.querySelector('[data-qty-number]');
      
      card.classList.remove('is-selected');
      if (addBtn) addBtn.classList.remove('hidden');
      if (qtyWrapper) qtyWrapper.classList.add('hidden');
      if (qtyNumber) qtyNumber.textContent = '1';
    }

    updateUI() {
      const totalSelected = this.getTotalSelectedCount();
      const remaining = this.totalItems - totalSelected;
      const isComplete = remaining <= 0;
      const progressPercent = Math.min((totalSelected / this.totalItems) * 100, 100);
      
      // Update counts
      if (this.selectedCountEl) {
        this.selectedCountEl.textContent = totalSelected;
      }
      if (this.mobileSelectedCount) {
        this.mobileSelectedCount.textContent = totalSelected;
      }
      
      // Update progress bar
      if (this.progressBar) {
        this.progressBar.style.width = `${progressPercent}%`;
      }
      if (this.mobileProgressBar) {
        this.mobileProgressBar.style.width = `${progressPercent}%`;
      }
      
      // Update remaining text
      if (this.remainingTextEl) {
        this.remainingTextEl.textContent = `Select ${remaining} more.`;
        this.remainingTextEl.classList.toggle('hidden', isComplete);
      }
      if (this.completeTextEl) {
        this.completeTextEl.classList.toggle('hidden', !isComplete);
      }
      if (this.mobileRemaining) {
        this.mobileRemaining.textContent = isComplete ? 'Bundle complete!' : `Select ${remaining} more.`;
        this.mobileRemaining.classList.toggle('is-complete', isComplete);
      }
      
      // Update slots
      this.updateSlots();
      
      // Update checkout visibility
      if (this.checkoutSection) {
        this.checkoutSection.classList.toggle('hidden', !isComplete);
      }
      if (this.selectMoreBtn) {
        this.selectMoreBtn.classList.toggle('hidden', isComplete);
        const btn = this.selectMoreBtn.querySelector('button');
        if (btn) {
          btn.textContent = `Select ${remaining} more.`;
        }
      }
      if (this.selectMoreBtnMobile) {
        this.selectMoreBtnMobile.classList.toggle('hidden', isComplete);
      }
      if (this.cartBtnMobile) {
        this.cartBtnMobile.classList.toggle('hidden', !isComplete);
      }
      if (this.mobileCheckout) {
        this.mobileCheckout.classList.toggle('hidden', !isComplete);
      }
      
      // Update cart buttons
      if (this.cartBtn) {
        this.cartBtn.disabled = !isComplete;
      }
      if (this.mobileCartBtn) {
        this.mobileCartBtn.disabled = !isComplete;
        if (this.mobileCartText) {
          this.mobileCartText.textContent = isComplete 
            ? `Add bundle to cart`
            : `Select ${remaining} more.`;
        }
      }

      // Auto-expand mobile panel when bundle is complete
      if (isComplete && this.mobileContent && this.mobileContent.classList.contains('hidden')) {
        this.toggleMobilePanel();
      }

      // Update prices
      this.updatePrices();
      
      // Update free gift badge visibility
      this.updateFreeGiftBadge();
    }

    updateFreeGiftBadge() {
      const showGift = this.freeGiftEnabled && this.purchaseType === 'subscription';
      
      if (this.freeGiftBadge) {
        this.freeGiftBadge.classList.toggle('hidden', !showGift);
      }
      if (this.freeGiftBadgeMobile) {
        this.freeGiftBadgeMobile.classList.toggle('hidden', !showGift);
      }
    }

    updateSlots() {
      // Clear all slots first
      this.slots.forEach(slot => {
        slot.classList.remove('is-filled');
        slot.removeAttribute('data-product-id');
        slot.removeAttribute('data-tier-color');
        slot.removeAttribute('data-product-color');
        
        const slotInner = slot.querySelector('.bundle-builder__slot-inner');
        const imageEl = slot.querySelector('[data-slot-image]');
        const removeBtn = slot.querySelector('[data-remove-slot]');
        const tierEl = slot.querySelector('[data-slot-tier]');
        
        if (slotInner) slotInner.style.backgroundColor = '';
        if (imageEl) {
          imageEl.innerHTML = '';
          imageEl.style.backgroundImage = '';
        }
        if (removeBtn) removeBtn.classList.add('hidden');
        if (tierEl) tierEl.textContent = '';
      });
      
      this.mobileSlots.forEach(slot => {
        slot.classList.remove('is-filled');
        slot.removeAttribute('data-product-id');
        
        const mobileSlotInner = slot.querySelector('.bundle-builder__mobile-slot-inner');
        const imageEl = slot.querySelector('[data-mobile-slot-image]');
        const removeBtn = slot.querySelector('[data-mobile-remove-slot]');
        const tierEl = slot.querySelector('[data-mobile-slot-tier]');
        
        if (mobileSlotInner) mobileSlotInner.style.backgroundColor = '';
        if (imageEl) imageEl.innerHTML = '';
        if (removeBtn) removeBtn.classList.add('hidden');
        if (tierEl) tierEl.textContent = '';
      });
      
      // Fill slots with selected items
      let slotIndex = 0;
      
      this.selectedItems.forEach(item => {
        for (let i = 0; i < item.quantity; i++) {
          if (slotIndex < this.slots.length) {
            const slot = this.slots[slotIndex];
            const mobileSlot = this.mobileSlots[slotIndex];
            
            // Desktop slot
            slot.classList.add('is-filled');
            slot.dataset.productId = item.productId;
            
            const imageEl = slot.querySelector('[data-slot-image]');
            const removeBtn = slot.querySelector('[data-remove-slot]');
            const tierEl = slot.querySelector('[data-slot-tier]');
            const slotInner = slot.querySelector('.bundle-builder__slot-inner');
            
            // Apply bar color from metafield
            if (slotInner && item.barColor) {
              slotInner.style.backgroundColor = item.barColor;
            }
            
            if (imageEl) {
              // Show product title in slot
              const shortTitle = item.title;
              imageEl.innerHTML = `<span style="color: white; font-size: 10px; text-align: center;">${shortTitle}</span>`;
            }
            if (removeBtn) removeBtn.classList.remove('hidden');
            if (tierEl) tierEl.textContent = item.tier || '';
            
            // Mobile slot
            if (mobileSlot) {
              mobileSlot.classList.add('is-filled');
              mobileSlot.dataset.productId = item.productId;
              
              const mobileSlotInner = mobileSlot.querySelector('.bundle-builder__mobile-slot-inner');
              const mobileImageEl = mobileSlot.querySelector('[data-mobile-slot-image]');
              const mobileRemoveBtn = mobileSlot.querySelector('[data-mobile-remove-slot]');
              const mobileTierEl = mobileSlot.querySelector('[data-mobile-slot-tier]');
              
              // Apply bar color from metafield
              if (mobileSlotInner && item.barColor) {
                mobileSlotInner.style.backgroundColor = item.barColor;
              }
              
              if (mobileImageEl) {
                const shortTitle = item.title.length > 15 ? item.title.substring(0, 15) + '...' : item.title;
                mobileImageEl.innerHTML = `<span>${shortTitle}</span>`;
              }
              if (mobileRemoveBtn) mobileRemoveBtn.classList.remove('hidden');
              if (mobileTierEl) mobileTierEl.textContent = item.tier || '';
            }
            
            slotIndex++;
          }
        }
      });
    }

    updatePrices() {
      const total = this.calculateTotal();
      const fullCompareTotal = this.calculateFullCompareTotal(); // Use full sum of compare prices

      // Desktop total
      if (this.bundlePriceEl) {
        this.bundlePriceEl.textContent = this.formatMoney(total);
      }

      // Desktop compare price - show full original price
      if (this.bundleCompareEl) {
        if (fullCompareTotal > total) {
          this.bundleCompareEl.textContent = this.formatMoney(fullCompareTotal);
          this.bundleCompareEl.classList.remove('hidden');
        } else {
          this.bundleCompareEl.classList.add('hidden');
        }
      }

      // Mobile total
      if (this.mobilePriceEl) {
        this.mobilePriceEl.textContent = this.formatMoney(total);
      }

      // Mobile compare price
      if (this.mobileComparePriceEl) {
        if (fullCompareTotal > total) {
          this.mobileComparePriceEl.textContent = this.formatMoney(fullCompareTotal);
          this.mobileComparePriceEl.classList.remove('hidden');
        } else {
          this.mobileComparePriceEl.classList.add('hidden');
        }
      }
    }


    calculateTotal() {
      // Get all individual item prices (expanded by quantity)
      let allPrices = [];
      this.selectedItems.forEach(item => {
        for (let i = 0; i < item.quantity; i++) {
          allPrices.push(item.price);
        }
      });
      
      // Sort prices from highest to lowest
      allPrices.sort((a, b) => b - a);
      
      // Remove the cheapest item (last one) if bundle is complete (5 items)
      // This makes the 5th item free
      if (allPrices.length >= this.totalItems) {
        allPrices.pop(); // Remove the cheapest item
      }
      
      // Calculate total from remaining items
      let total = allPrices.reduce((sum, price) => sum + price, 0);
      
      if (this.purchaseType === 'subscription') {
        total = total * (1 - this.subscriptionDiscount / 100);
      }
      
      return Math.round(total);
    }

    calculateFullCompareTotal() {
      let total = 0;
      this.selectedItems.forEach(item => {
        total += item.comparePrice * item.quantity;
      });
      return total;
    }

    calculateCompareTotal() {
      // Get all individual item prices (expanded by quantity)
      let allPrices = [];
      console.log('this.selectedItems');
      console.log(this.selectedItems);
      this.selectedItems.forEach(item => {
        for (let i = 0; i < item.quantity; i++) {
          allPrices.push(item.price);
        }
      });
      
      // Sort prices from highest to lowest
      allPrices.sort((a, b) => b - a);
      
      // Remove the cheapest item (last one) if bundle is complete (5 items)
      if (allPrices.length >= this.totalItems) {
        allPrices.pop(); // Remove the cheapest item
      }
      
      // Return total without subscription discount (for compare price)
      return allPrices.reduce((sum, price) => sum + price, 0);
    }

    formatMoney(cents) {
      return '$' + (cents / 100).toFixed(0);
    }

    setPurchaseType(type) {
      this.purchaseType = type;
      
      // Update button states
      this.purchaseOptionBtns.forEach(btn => {
        btn.classList.toggle('bundle-builder__option-btn--active', btn.dataset.purchaseOption === type);
      });
      
      this.mobilePurchaseOptionBtns.forEach(btn => {
        btn.classList.toggle('bundle-builder__option-btn--active', btn.dataset.mobilePurchaseOption === type);
      });
      
      // Toggle frequency visibility
      const showFrequency = type === 'subscription';
      if (this.frequencyWrapper) {
        this.frequencyWrapper.classList.toggle('hidden', !showFrequency);
      }
      if (this.mobileFrequencyWrapper) {
        this.mobileFrequencyWrapper.classList.toggle('hidden', !showFrequency);
      }
      
      this.updatePrices();
      this.updateFreeGiftBadge();
    }

    toggleMobilePanel() {
      const isExpanded = this.mobileToggle.getAttribute('aria-expanded') === 'true';
      this.mobileToggle.setAttribute('aria-expanded', !isExpanded);
      this.mobileContent.classList.toggle('hidden', isExpanded);
    }

    async addToCart() {
      if (this.getTotalSelectedCount() < this.totalItems) {
        return;
      }
      
      const items = [];
      // Generate a unique bundle ID that will be shared across all items in this bundle
      const bundleId = `bundle-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      // Generate a bundle creation timestamp (shared across all items)
      const bundleTimestamp = Date.now().toString();
      // Generate a bundle form ID (shared across all items)
      const bundleFormId = `bundle-form-${bundleTimestamp}`;

      let dataGood = true;

      // Track bundle items with their positions for integrity checking
      let itemIndex = 0;
      this.selectedItems.forEach(item => {
        const cartItem = {
          id: parseInt(item.variantId),
          quantity: item.quantity,
          properties: {
            '_bundle': 'true',
            '_bundle_id': bundleId,
            // Appstle bundle discount scripts commonly group items using a `bundle` property.
            // When multiple bundles are in cart, using a unique value per bundle instance
            // helps ensure discounts are applied per-bundle (not just once per cart).
            'bundle': bundleId,
            '_bundle_form_id': bundleFormId,
            '_is_bundle_item': 'true',
            '_bundle_timestamp': bundleTimestamp,
            '_bundle_item_index': itemIndex.toString(), // Unique position in bundle
            '_bundle_item_count': this.selectedItems.length.toString() // Total items in bundle
          }
        };
        itemIndex++;

        if (this.purchaseType === 'subscription') {

          let subscriptionPlanId = "";

          const subscriptionPlanElement = this.querySelector('[data-frequency-select]');
          if(subscriptionPlanElement){
            subscriptionPlanId = subscriptionPlanElement.value;
            if(subscriptionPlanId){
              const sellingPlanData = JSON.parse(item.sellingPlan);
              let sellingPlanFound = "";
              let sellingPlan = "";
              sellingPlanData ? sellingPlanData.forEach(e=>{e.id == subscriptionPlanId ? sellingPlanFound = e.selling_plans[0].id : ""}) : dataGood = false;
              sellingPlanFound ? sellingPlan = sellingPlanFound : dataGood = false;

              cartItem.selling_plan = sellingPlan;
            } 
          }        
          // cartItem.selling_plan = item.sellingId;
        }
        
        items.push(cartItem);
      });

      // Free gift will be added separately after bundle items
      const addFreeGift = this.purchaseType === 'subscription' && this.freeGiftEnabled && this.freeGiftVariantId;

      if(!dataGood) return;


      // Disable buttons during submission
      if (this.cartBtn) this.cartBtn.disabled = true;
      if (this.mobileCartBtn) this.mobileCartBtn.disabled = true;
      
      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({ items })
        });

        if (!response.ok) {
          throw new Error('Failed to add to cart');
        }

        const data = await response.json();

        // Register bundle with BundleTracker immediately upon creation
        if (window.bundleTracker) {
          // Register bundle BEFORE refresh to track from initial creation
          window.bundleTracker.registerBundle(bundleId, this.selectedItems.length, bundleFormId);
          
          // Wait for cart API to process, then validate
          setTimeout(async () => {
            try {
              // Refresh to sync with actual cart state
              await window.bundleTracker.refresh();
              
              // Validate bundle integrity after refresh
              const result = await window.bundleTracker.validateBundleIntegrity(bundleId);
              
              if (!result.valid) {
                console.error('BundleBuilder: Bundle integrity check failed after creation', {
                  bundleId: bundleId,
                  result: result,
                  expectedCount: this.selectedItems.length,
                  actualCount: result.actualCount
                });
              } else {
                console.log('BundleBuilder: Bundle successfully created and validated', {
                  bundleId: bundleId,
                  itemCount: result.actualCount
                });
              }
            } catch (error) {
              console.error('BundleBuilder: Error validating bundle after creation', error);
            }
          }, 1000); // Increased delay to ensure cart API has processed
        }

        // Add free gift as a separate item (not part of the bundle)
        if (addFreeGift) {
          try {
            const giftResponse = await fetch('/cart/add.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
              },
              body: JSON.stringify({
                items: [{
                  id: parseInt(this.freeGiftVariantId),
                  quantity: 1,
                  properties: {
                    '_free_gift': 'true',
                    '_subscription_gift': 'true'
                  }
                }]
              })
            });
            
            if (!giftResponse.ok) {
              console.warn('Failed to add free gift to cart');
            }
          } catch (giftError) {
            console.warn('Error adding free gift:', giftError);
          }
        }

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: "Bundle_products",
          productVariantId: items[0]?.id,
          cartData: data
        })


        const cart = document.querySelector('mini-cart');
        if (cart) {
          const sections = cart.getSectionsToRender().map((section) => section.section);

          const cartResponse = await fetch(`${window.location.pathname}?sections=${sections.join(',')}`);
          const cartData = await cartResponse.json();

          cart.renderContents({ sections: cartData });

          cart.open?.();
        } 

        if (this.cartBtn) this.cartBtn.disabled = false;
        if (this.mobileCartBtn) this.mobileCartBtn.disabled = false;
        
      } catch (error) {
        console.error('Error adding bundle to cart:', error);
        alert('There was an error adding the bundle to your cart. Please try again.');
        
        // Re-enable buttons
        if (this.cartBtn) this.cartBtn.disabled = false;
        if (this.mobileCartBtn) this.mobileCartBtn.disabled = false;
      }
    }
  }

  customElements.define('bundle-builder', BundleBuilder);
}
